import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { ElementTypes } from '../enums/ElementTypes';
import { IBaseElement } from '../interfaces';
import { ElementButtonService, CreatorActionsService, CreatorStateService } from '../services/app-services';
import { GuidWrapper } from '../types';
import { BaseElementManager } from './base-element.manager';
import { ShapeAttributesManager } from './shape-attributes.manager';

@Injectable()
export class StaircaseManager {

  public static elementButtonService: ElementButtonService;

  /** SvgUtils staircase viewBox 44×66 with background-size: 100% auto. */
  private static readonly SEGMENT_ASPECT_RATIO = 66 / 44;

  private static readonly selectedSegmentByGuid = new Map<string, number>();

  public static withButtons(buttonService: ElementButtonService): void {
    this.elementButtonService = buttonService;
  }

  public static isStaircaseElement(el: HTMLElement): boolean {
    if (!el) {
      return false;
    }

    const guid = ShapeAttributesManager.getGUID(el);
    const baseElement = CreatorStateService.seatsPlan.baseElements?.find((item) => item.guid === guid);

    if (!baseElement) {
      return false;
    }

    const shape = CreatorStateService.shapes?.find((item) => item.id === baseElement.shapeId);

    return shape?.shapeElementType === ElementTypes.STAIRCASE;
  }

  public static addButtons(el: HTMLElement): void {
    if (!this.elementButtonService || !el || !this.isStaircaseElement(el)) {
      return;
    }

    this.elementButtonService.addButtonsToElement(
      el,
      'vertical',
      ShapeAttributesManager.getGUID(el)
    );
  }

  public static removeButtons(el: HTMLElement): void {
    this.elementButtonService?.removeButtons(el);
  }

  public static getSegmentMetrics(el: HTMLElement): {
    currentTop: number;
    currentHeight: number;
    segmentSize: number;
  } {
    const width = el.offsetWidth;

    return {
      segmentSize: Math.max(1, Math.round(width * this.SEGMENT_ASPECT_RATIO)),
      currentTop: el.style.top ? parseInt(el.style.top, 10) : el.offsetTop,
      currentHeight: el.style.height ? parseInt(el.style.height, 10) : el.offsetHeight,
    };
  }

  public static getSegmentCount(el: HTMLElement): number {
    const { currentHeight, segmentSize } = this.getSegmentMetrics(el);

    return Math.max(1, Math.round(currentHeight / segmentSize));
  }

  public static segmentIndexAtLocalY(el: HTMLElement, localY: number): number {
    const { segmentSize } = this.getSegmentMetrics(el);
    const count = this.getSegmentCount(el);

    return Math.max(0, Math.min(count - 1, Math.floor(localY / segmentSize)));
  }

  public static selectSegment(el: HTMLElement, index: number): void {
    if (!el || !this.isStaircaseElement(el)) {
      return;
    }

    const guid = ShapeAttributesManager.getGUID(el);
    const count = this.getSegmentCount(el);
    const clamped = Math.max(0, Math.min(count - 1, index));

    this.clearSegmentSelection();
    this.selectedSegmentByGuid.set(guid, clamped);
    this.syncSegmentHighlight(el);
  }

  public static clearSegmentSelection(guid?: string): void {
    if (guid) {
      this.selectedSegmentByGuid.delete(guid);
      const el = BaseElementManager.getHTMLElement(guid);

      if (el) {
        this.syncSegmentHighlight(el);
      }

      return;
    }

    const guids = Array.from(this.selectedSegmentByGuid.keys());
    this.selectedSegmentByGuid.clear();

    for (const selectedGuid of guids) {
      const el = BaseElementManager.getHTMLElement(selectedGuid);

      if (el) {
        this.syncSegmentHighlight(el);
      }
    }
  }

  public static syncSegmentHighlight(el: HTMLElement): void {
    if (!el) {
      return;
    }

    const guid = ShapeAttributesManager.getGUID(el);
    const index = this.selectedSegmentByGuid.get(guid);

    if (index == null || !this.isStaircaseElement(el)) {
      el.classList.remove('staircase-segment-selected');
      el.style.removeProperty('--staircase-segment-top');
      el.style.removeProperty('--staircase-segment-size');
      return;
    }

    const { segmentSize } = this.getSegmentMetrics(el);

    el.classList.add('staircase-segment-selected');
    el.style.setProperty('--staircase-segment-top', `${index * segmentSize}px`);
    el.style.setProperty('--staircase-segment-size', `${segmentSize}px`);
  }

  public static addSegment(el: HTMLElement, direction: 'front' | 'back'): {
    previous: GuidWrapper<IBaseElement>;
    current: GuidWrapper<IBaseElement>;
  } {
    return direction === 'front'
      ? this.addSegmentTowardFront(el)
      : this.addSegmentTowardBack(el);
  }

  public static addSegmentTowardFront(el: HTMLElement): {
    previous: GuidWrapper<IBaseElement>;
    current: GuidWrapper<IBaseElement>;
  } {
    const { currentTop, currentHeight, segmentSize } = this.getSegmentMetrics(el);

    return this.applySegmentUpdate(el, currentTop - segmentSize, currentHeight + segmentSize);
  }

  public static addSegmentTowardBack(el: HTMLElement): {
    previous: GuidWrapper<IBaseElement>;
    current: GuidWrapper<IBaseElement>;
  } {
    const { currentTop, currentHeight, segmentSize } = this.getSegmentMetrics(el);

    return this.applySegmentUpdate(el, currentTop, currentHeight + segmentSize);
  }

  public static removeSegment(el: HTMLElement, index: number): {
    previous: GuidWrapper<IBaseElement>;
    current: GuidWrapper<IBaseElement>;
  } | null {
    const count = this.getSegmentCount(el);

    if (count <= 1) {
      return null;
    }

    const { currentTop, currentHeight, segmentSize } = this.getSegmentMetrics(el);
    const clamped = Math.max(0, Math.min(count - 1, index));
    const top = clamped === 0 ? currentTop + segmentSize : currentTop;
    const height = currentHeight - segmentSize;
    const result = this.applySegmentUpdate(el, top, height);
    const newCount = this.getSegmentCount(el);
    const guid = ShapeAttributesManager.getGUID(el);

    if (newCount <= 1) {
      this.clearSegmentSelection(guid);
    } else {
      this.selectSegment(el, Math.min(clamped, newCount - 1));
    }

    return result;
  }

  public static tryRemoveSelectedSegment(el: HTMLElement): {
    previous: GuidWrapper<IBaseElement>;
    current: GuidWrapper<IBaseElement>;
  } | null {
    if (!this.isStaircaseElement(el)) {
      return null;
    }

    const index = this.selectedSegmentByGuid.get(ShapeAttributesManager.getGUID(el));

    if (index == null) {
      return null;
    }

    return this.removeSegment(el, index);
  }

  private static applySegmentUpdate(
    el: HTMLElement,
    top: number,
    height: number
  ): {
    previous: GuidWrapper<IBaseElement>;
    current: GuidWrapper<IBaseElement>;
  } {
    const previous = cloneDeep(BaseElementManager.getBaseElement(ShapeAttributesManager.getGUID(el)));

    el.style.top = `${top}px`;
    el.style.height = `${height}px`;

    const updated = CreatorActionsService.updateBaseElements([el]);

    return {
      previous,
      current: updated[0],
    };
  }
}
